import { LocaleInfo } from '@/objects/LocaleInfo';
import i18next from 'i18next';
import { defineStore, storeToRefs } from 'pinia';
import { ref, type Ref } from 'vue';

// A lookup for all our languages and important data
const locales = [] as LocaleInfo[];
locales.push(new LocaleInfo('nb', 'no', 'Norsk', 160, 'no', 'nb_NO', ['nb-no', 'no', 'nb', 'nn'], 'no'));
locales.push(new LocaleInfo('en', 'en', 'English', 225, 'us', 'en_US', ['en'], 'en'));
locales.push(new LocaleInfo('sv', 'sv', 'Svenska', 205, 'se', 'sv_SE', ['sv', 'se'], 'sv'));
locales.push(new LocaleInfo('it', 'it', 'Italiano', 105, 'it', 'it_IT', ['it'], 'it'));
locales.push(new LocaleInfo('fi', 'fi', 'Suomi', 72, 'fi', 'fi_FI', ['fi'], 'fi'));
locales.push(new LocaleInfo('ru', 'ru', 'Россия', 0, 'ru', 'ru_RU', ['ru', 'tt'], 'ru'));
locales.push(new LocaleInfo('fr', 'fr', 'Français', 0, 'fr', 'fr_FR', ['fr'], 'fr'));
locales.push(new LocaleInfo('de', 'de', 'Deutsch', 0, 'de', 'de_DE', ['de'], 'de'));
locales.push(new LocaleInfo('es', 'es', 'Español', 199, 'es', 'es_ES', ['es'], 'es'));
locales.push(new LocaleInfo('et', 'et', 'Eesti', 67, 'ee', 'et_EE', ['et', 'ee'], 'et'));

// Filter out the active locales ( We only support NO, EN, SV for now)
const activeLocales = locales.filter((x) => ['no', 'en', 'sv'].includes(x.Base));

// Define a store to handle the locale
export const useLocaleStore = defineStore('locale', () => {
    /**
     * Get a locale by language code
     * @param language
     * @returns
     */
    const getLocaleByCode = (language: string): LocaleInfo | null => {
        const localeProps = language.split('-');
        // Get Major language
        language = localeProps[0];

        const items = locales.filter((x) => x.Codes.indexOf(language!.toLowerCase()) >= 0);
        if (!items.length) return null;

        return items[0];
    };

    /**
     * Set the current locale and trigger a 18n change
     * @param language
     */
    const setLocale = (language: string) => {
        const localeInfo = getLocaleByCode(language);
        if (localeInfo) {
            locale.value = localeInfo;
            i18next.changeLanguage(localeInfo.Locale);
        }
    };

    /**
     * Find the preffered user language based on browser or previous selected setting
     * @returns
     */
    const getPreferredLanguage = (): LocaleInfo => {
        // Get the browser locale
        const userLocale = getLocaleByCode(navigator.language);

        // Get the saved i18next value
        const i18nLocale = getLocaleByCode(localStorage.getItem('i18nextLng') ?? '');

        return i18nLocale ?? userLocale ?? getLocaleByCode('en')!;
    };

    /**
     * The current selected locale
     */
    const locale: Ref<LocaleInfo> = ref<LocaleInfo>(getPreferredLanguage());

    return {
        /**
         * The current selected locale
         */
        locale,

        /**
         * All available locales
         */
        locales: activeLocales,

        /**
         * Sets the locale
         * @param language
         * @returns
         */
        setLocale: (language: string) => setLocale(language),
    };
});

/**
 * A reactive reference to the locale store
 * @returns
 */
export const useLocaleStoreRef = () => storeToRefs(useLocaleStore());
